class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartExpiryDurationMs = 5 * 60 * 1000;
    this.cartExpiryStorageKey = 'bump_cart_drawer_expire_ends_at';
    this.cartExpiryIntervalId = null;
    this.bindCartExpiryTimerElements();
    this.resumeCartExpiryTimer();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.bindCartExpiryTimerElements();
    this.resumeCartExpiryTimer();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.bindCartExpiryTimerElements();
    this.startCartExpiryTimer();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  bindCartExpiryTimerElements() {
    this.cartExpiryTimer = this.querySelector('[data-cart-expire-timer]');
    this.cartExpiryTimerValue = this.querySelector('[data-cart-expire-timer-value]');
  }

  getCartExpiryStorage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  getStoredCartExpiryEndsAt() {
    const storage = this.getCartExpiryStorage();
    if (!storage) return NaN;
    return Number.parseInt(storage.getItem(this.cartExpiryStorageKey) || '', 10);
  }

  setStoredCartExpiryEndsAt(endsAt) {
    const storage = this.getCartExpiryStorage();
    if (!storage) return;
    storage.setItem(this.cartExpiryStorageKey, String(endsAt));
  }

  clearStoredCartExpiryEndsAt() {
    const storage = this.getCartExpiryStorage();
    if (!storage) return;
    storage.removeItem(this.cartExpiryStorageKey);
  }

  hideCartExpiryTimer() {
    if (!this.cartExpiryTimer) return;
    this.cartExpiryTimer.hidden = true;
  }

  showCartExpiryTimer() {
    if (!this.cartExpiryTimer) return;
    this.cartExpiryTimer.hidden = false;
  }

  formatCartExpiryTime(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  runCartExpiryTimer(endsAt) {
    if (!this.cartExpiryTimer || !this.cartExpiryTimerValue) return;

    if (this.cartExpiryIntervalId) {
      window.clearInterval(this.cartExpiryIntervalId);
      this.cartExpiryIntervalId = null;
    }

    const tick = () => {
      const remainingSeconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));

      if (remainingSeconds <= 0) {
        this.hideCartExpiryTimer();
        this.clearStoredCartExpiryEndsAt();
        if (this.cartExpiryIntervalId) {
          window.clearInterval(this.cartExpiryIntervalId);
          this.cartExpiryIntervalId = null;
        }
        return;
      }

      this.cartExpiryTimerValue.textContent = this.formatCartExpiryTime(remainingSeconds);
      this.showCartExpiryTimer();
    };

    tick();
    this.cartExpiryIntervalId = window.setInterval(tick, 1000);
  }

  resumeCartExpiryTimer() {
    const endsAt = this.getStoredCartExpiryEndsAt();
    if (!Number.isFinite(endsAt) || endsAt <= Date.now()) {
      this.hideCartExpiryTimer();
      this.clearStoredCartExpiryEndsAt();
      return;
    }
    this.runCartExpiryTimer(endsAt);
  }

  startCartExpiryTimer() {
    const endsAt = Date.now() + this.cartExpiryDurationMs;
    this.setStoredCartExpiryEndsAt(endsAt);
    this.runCartExpiryTimer(endsAt);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
